import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { useAppContext } from '@/hooks/useAppContext';
import { useSocial } from '@/contexts/SocialContext';
import { useFeed } from '@/contexts/FeedContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { UserAvatar, pubkeyToNpub } from '@/components/user';
import { getDisplayName } from '@/lib/getDisplayName';
import { useAuthor } from '@/hooks/useAuthor';
import { normalizeRelayUrl, isWebsocketUrl } from '@/lib/jumble';
import { Trash2, Moon, Sun } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

function ThemeRow() {
  const { theme, setTheme } = useTheme();
  const dark = theme === 'dark';
  return (
    <div className="flex items-center justify-between py-2">
      <Label className="flex items-center gap-2">{dark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} Dark mode</Label>
      <Switch checked={dark} onCheckedChange={(v) => setTheme(v ? 'dark' : 'light')} />
    </div>
  );
}

function RelayManager() {
  const { config, updateConfig } = useAppContext();
  const [url, setUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const relays = config.relayMetadata.relays;

  const save = (next: typeof relays) => {
    updateConfig(() => ({ relayMetadata: { relays: next, updatedAt: Math.floor(Date.now() / 1000) } }));
  };

  return (
    <div>
      {relays.map((r) => (
        <div key={r.url} className="flex items-center gap-2 border-b py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-mono text-xs">{r.url}</span>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={r.read} onChange={() => save(relays.map((x) => x.url === r.url ? { ...x, read: !x.read } : x))} /> read
          </label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={r.write} onChange={() => save(relays.map((x) => x.url === r.url ? { ...x, write: !x.write } : x))} /> write
          </label>
          <Button size="icon" variant="ghost" aria-label="Remove relay" onClick={() => save(relays.filter((x) => x.url !== r.url))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <div className="mt-2 flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="wss://…" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <Button onClick={add}>Add</Button>
      </div>
      {err && <p className="mt-1 text-sm text-destructive">{err}</p>}
    </div>
  );

  function add() {
    setErr(null);
    const raw = url.trim();
    if (!raw) return;
    const candidate = raw.startsWith('ws') ? raw : `wss://${raw}`;
    if (!isWebsocketUrl(candidate)) { setErr('Invalid relay URL'); return; }
    const normalized = normalizeRelayUrl(candidate);
    if (relays.some((r) => r.url === normalized)) { setErr('Relay already added'); return; }
    save([...relays, { url: normalized, read: true, write: true }]);
    setUrl('');
  }
}

function MuteManager() {
  const { mutedPubkeys, toggleMutePubkey, mutedWords, addMutedWord, removeMutedWord, showReplies, setShowReplies } = useSocial();
  const [word, setWord] = useState('');
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between py-1">
        <Label>Show replies in feeds</Label>
        <Switch checked={showReplies} onCheckedChange={setShowReplies} />
      </div>
      <div>
        <Label>Muted words ({mutedWords.length})</Label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {mutedWords.map((w) => (
            <button key={w} onClick={() => removeMutedWord(w)} className="rounded-full bg-muted px-3 py-1 text-xs hover:bg-destructive/20" title="Remove">{w} ✕</button>
          ))}
          {mutedWords.length === 0 && <span className="text-xs text-muted-foreground">None</span>}
        </div>
        <div className="mt-2 flex gap-2">
          <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="Add muted word…" onKeyDown={(e) => { if (e.key === 'Enter') { addMutedWord(word); setWord(''); } }} />
          <Button size="sm" onClick={() => { addMutedWord(word); setWord(''); }}>Mute</Button>
        </div>
      </div>
      <div>
        <Label>Muted users ({mutedPubkeys.length})</Label>
        {mutedPubkeys.length === 0 ? <p className="text-xs text-muted-foreground">None</p> : (
          <ul className="mt-1 flex flex-col gap-1">
            {mutedPubkeys.map((pk) => (
              <MutedRow key={pk} pubkey={pk} onUnmute={() => toggleMutePubkey(pk)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MutedRow({ pubkey, onUnmute }: { pubkey: string; onUnmute: () => void }) {
  const author = useAuthor(pubkey);
  return (
    <li className="flex items-center gap-2 text-sm">
      <UserAvatar pubkey={pubkey} size="xs" />
      <span className="flex-1 truncate">{getDisplayName(author.data?.metadata, pubkey)}</span>
      <Button size="sm" variant="ghost" onClick={onUnmute}>Unmute</Button>
    </li>
  );
}

export function SettingsPage() {
  useSeoMeta({ title: 'Settings — Jumble', description: 'Relays, appearance, content filters and account.' });
  const { user } = useCurrentUser();
  const { pinnedPubkeys, togglePin } = useSocial();
  const { relaySets } = useFeed();
  void relaySets;

  return (
    <AppShell title="Settings">
      <div className="flex flex-col gap-4 p-4 pb-24 md:pb-8">
        <Card className="shadow-none">
          <CardContent className="p-4">
            <h3 className="font-extrabold">Account</h3>
            {user ? (
              <div className="mt-2 flex items-center gap-2">
                <UserAvatar pubkey={user.pubkey} size="sm" />
                <Link to={`/${pubkeyToNpub(user.pubkey)}`} className="flex-1 truncate text-sm font-semibold hover:underline">
                  <ProfileName pubkey={user.pubkey} />
                </Link>
              </div>
            ) : null}
            <div className="mt-3"><LoginArea className="w-full max-w-none" /></div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardContent className="p-4">
            <h3 className="font-extrabold">Appearance</h3>
            <ThemeRow />
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardContent className="p-4">
            <h3 className="font-extrabold">Relays (NIP-65 read/write)</h3>
            <p className="text-xs text-muted-foreground">Used for your identity queries and publishing. Relay-set feeds are separate.</p>
            <div className="mt-2"><RelayManager /></div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardContent className="p-4">
            <h3 className="font-extrabold">Content & filters</h3>
            <div className="mt-2"><MuteManager /></div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardContent className="p-4">
            <h3 className="font-extrabold">Pinned users ({pinnedPubkeys.length})</h3>
            {pinnedPubkeys.length === 0 ? <p className="text-xs text-muted-foreground">Pin people to keep them handy.</p> : (
              <ul className="mt-1 flex flex-col gap-1">
                {pinnedPubkeys.map((pk) => (
                  <MutedRow key={pk} pubkey={pk} onUnmute={() => togglePin(pk)} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Separator />
        <p className="text-xs text-muted-foreground">
          Jumble clone built on Nostr + Nostrify. Vibed with <a className="underline" href="https://shakespeare.diy" target="_blank" rel="noreferrer">Shakespeare</a>.
        </p>
      </div>
    </AppShell>
  );
}

function ProfileName({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  return <>{getDisplayName(author.data?.metadata, pubkey)}</>;
}
